import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { SearchableSelect, SelectOption } from '@/components/SearchableSelect';
import { Switch } from '@/components/settings/SettingsPage';
import { BotHealthCard } from './BotHealthCard';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { HealthCard } from '@/components/HealthCard';
import { ModelPicker, ModelPickerValue } from '@/components/ModelPicker';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import { whatsappAccountService, WhatsappAccount, AiBotConfig, AiBotRequiredField, AiBotFieldType, AiBotUpsellItem, UpsellWhenToOffer } from '@/services/whatsapp-account.service';
import { departmentsService, Department } from '@/services/departments.service';
import { aiAgentsService, AiAgent } from '@/services/ai-agents.service';
import { MediaUpload } from '@/components/MediaUpload';
import { Phone, Shield, Globe, Mail, MapPin, Info, Bot, Sparkles, MessageCircle, PowerOff, Workflow, Plus, Trash2, GripVertical, Download, ListChecks, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export const DEFAULT_AI_CONFIG: AiBotConfig = {
  instructions: '',
  language: 'en',
  maxResponseChars: 600,
  refusalStyle: 'soft',
  captureLeadEnabled: true,
  greeting: '',
  services: [],
  priceList: [],
  linkedDataUids: [],
};

const BUSINESS_CATEGORIES: SelectOption[] = Object.entries({
  UNDEFINED: 'Not specified', AUTO: 'Automotive', BEAUTY: 'Beauty and personal care', APPAREL: 'Clothing and accessories', EDU: 'Education',
  ENTERTAIN: 'Entertainment', EVENT_PLAN: 'Event planning', FINANCE: 'Finance', GROCERY: 'Grocery', GOVT: 'Government', HOTEL: 'Hotel and lodging',
  HEALTH: 'Health', NONPROFIT: 'Nonprofit', PROF_SERVICES: 'Professional services', RETAIL: 'Retail', TRAVEL: 'Travel and transportation',
  RESTAURANT: 'Restaurant', OTHER: 'Other', NOT_A_BIZ: 'Not a business',
}).map(([value, label]) => ({ value, label }));

const CONVERSATION_MODES: SelectOption[] = [
  { value: '', label: 'Auto', description: 'Picked from your business category' },
  { value: 'intake', label: 'Lead intake', description: 'Qualify a sales lead — IT, agencies, consulting' },
  { value: 'order', label: 'Order-taking', description: 'Cart → upsell → confirm → details — restaurant, retail' },
  { value: 'booking', label: 'Appointment booking', description: 'Service + date/time + contact — salon, clinic, viewings' },
  { value: 'support', label: 'Support / FAQ', description: 'Answer from your info, escalate to a human — telecom, ISP' },
];

/** A compact on/off row: switch, title, one-line explanation. */
function ToggleRow({ checked, onChange, title, hint, disabled, icon }: { checked: boolean; onChange: (next: boolean) => void; title: string; hint?: React.ReactNode; disabled?: boolean; icon?: React.ReactNode }) {
  return (
    <div className={clsx('flex items-start gap-3 px-3 py-2.5', disabled && 'opacity-60')}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold text-text-primary">{icon}{title}</div>
        {hint && <div className="mt-0.5 text-xs leading-snug text-text-muted">{hint}</div>}
      </div>
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={title} />
    </div>
  );
}

type Props = {
  /** Rendered inside WhatsappAccountsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function WhatsappAccountSettings({ embedded = false }: Props = {}) {
  const [searchParams] = useSearchParams();
  const accountUid = searchParams.get('account');
  const [acc, setAcc] = useState<WhatsappAccount | null>(null);
  const confirmedAccount = useRef<WhatsappAccount | null>(null);
  const acceptAccount = (value: WhatsappAccount) => { confirmedAccount.current = value; setAcc(value); };
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState('');
  const [loadError, setLoadError] = useState(false);
  // An AI agent assigned to this number replaces its AI settings unless the agent borrows this very number.
  const [assignedAgent, setAssignedAgent] = useState<AiAgent | null>(null);
  const aiAgentUid = acc?.aiAgentUid;
  useEffect(() => {
    setAssignedAgent(null);
    if (aiAgentUid) aiAgentsService.getQuiet(aiAgentUid).then(setAssignedAgent);
  }, [aiAgentUid]);
  const agentBorrowsThisNumber = !!assignedAgent && assignedAgent.brainAccountUid === acc?.accountUid;

  useEffect(() => {
    setAcc(null);
    confirmedAccount.current = null;
    const loader = accountUid
      ? whatsappAccountService.getById(accountUid)
      : whatsappAccountService.get();
    setLoadError(false);
    let cancelled = false;
    loader.then(value => { if (!cancelled) acceptAccount(value); }).catch(() => { if (!cancelled) setLoadError(true); });
    return () => { cancelled = true; };
  }, [accountUid]);

  const save = async () => {
    if (!acc) return;
    setBusy(true);
    try {
      const payload: Partial<WhatsappAccount> = {
        maxFlowRetries: acc.maxFlowRetries,
        flowRetryWindowMinutes: acc.flowRetryWindowMinutes,
        botMode: acc.botMode,
        captureDepartmentUid: acc.captureDepartmentUid,
        // Send the full subdoc — Mongoose $set replaces the whole object.
        // Backend trusts the body so frontend MUST send the complete config.
        aiBotConfig: acc.aiBotConfig,
      };
      for (const key of ['businessName', 'emailAddress', 'website', 'aboutStatus', 'address', 'profilePicUrl', 'profileImagekitFileId', 'businessDescription', 'businessCategory'] as const) {
        if (acc[key] !== confirmedAccount.current?.[key]) payload[key] = acc[key] ?? '';
      }
      const updated = accountUid
        ? await whatsappAccountService.updateProfileById(accountUid, payload)
        : await whatsappAccountService.updateProfile(payload);
      acceptAccount(updated);
      toast.success('Changes saved successfully');
    } catch {
      const loader = accountUid ? whatsappAccountService.getById(accountUid) : whatsappAccountService.get();
      const saved = await loader.catch(() => null);
      if (saved) acceptAccount(saved);
    } finally { setBusy(false); }
  };

  const syncFromMeta = async () => {
    setBusy(true);
    try {
      const updated = await whatsappAccountService.syncProfile(accountUid ?? undefined);
      acceptAccount(updated);
      toast.success('Latest WhatsApp profile synced to CRM');
    } catch { /* axios interceptor toasts */ } finally { setBusy(false); }
  };

  if (!acc) {
    return (
      <>
        {!embedded && <Topbar title="WhatsApp Account" breadcrumb="WhatsApp" />}
        <div className={clsx('w-full space-y-3 px-6', embedded ? 'pb-24 pt-3' : 'py-5')}>
          {loadError ? <Card><p className="text-sm text-text-muted">The account could not be loaded. Select a connected number from Accounts and try again.</p></Card> : <><div className="skeleton h-24 rounded-2xl" /><div className="skeleton h-64 rounded-2xl" /></>}
        </div>
      </>
    );
  }

  return (
    <>
      {embedded ? (
        // The tab shell owns the page title; this strip names which number is
        // being edited, which the Topbar used to carry.
        <div className="flex flex-wrap items-center gap-2 px-6 pt-3 text-xs text-text-muted">
          <span className="font-medium text-text-primary">{acc.businessName}</span>
          <span>·</span>
          <span>{acc.displayPhoneNumber}</span>
          {acc.isDefault && (
            <span className="rounded-full bg-primary-soft px-2 py-0.5 text-2xs font-semibold text-primary">Default</span>
          )}
        </div>
      ) : (
        <Topbar
          title={accountUid ? acc.businessName : 'WhatsApp Account'}
          breadcrumb={accountUid ? 'WhatsApp / Accounts' : 'WhatsApp'}
          description={
            accountUid
              ? `Editing ${acc.displayPhoneNumber}${acc.isDefault ? ' (default account)' : ''}`
              : 'Manage your WhatsApp Business profile and connection settings.'
          }
        />
      )}

      <div className={clsx('w-full space-y-3 px-6', embedded ? 'pb-24 pt-2' : 'py-5')}>
        {acc.profileSyncStatus === 'failed' && <div role="alert" className="rounded-xl border border-danger/25 bg-danger-soft p-4 text-sm"><strong>WhatsApp did not accept the profile change.</strong><p className="mt-1">{acc.profileSyncError}</p><p className="mt-1 text-xs">Your last confirmed profile is shown. The unsuccessful changes are saved as a draft.</p>{acc.profileDraft && <Button className="mt-3" variant="secondary" size="sm" onClick={() => setAcc({ ...acc, ...acc.profileDraft })}>Restore draft for editing</Button>}</div>}
        {/* Health + Phone row */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <HealthCard
            key={acc.accountUid}
            accountUid={acc.accountUid}
            initial={{
              qualityRating: acc.qualityRating,
              messagingLimitTier: acc.messagingLimitTier,
              fetchedAt: acc.healthFetchedAt ?? new Date(0).toISOString(),
              staleSince: acc.healthError || !acc.healthFetchedAt ? acc.healthFetchedAt ?? new Date(0).toISOString() : undefined,
              error: acc.healthError,
            }}
          />

          <Card>
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                <Phone size={17} />
              </div>
              <div>
                <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Phone</div>
                <div className="mt-0.5 font-display text-xl font-bold tracking-tight text-text-primary tabular-nums">
                  {acc.displayPhoneNumber}
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-text-muted">
              Verified as <strong className="text-text-secondary">{acc.verifiedName}</strong>
            </div>
          </Card>
        </div>

        <BotHealthCard
          account={acc}
          onAccountChange={(updated) => {
            confirmedAccount.current = updated;
            setAcc(prev => prev ? { ...prev, isActive: updated.isActive, isDefault: updated.isDefault, botMode: updated.botMode, aiBotCompiledPrompt: updated.aiBotCompiledPrompt, aiBotPromptUpdatedAt: updated.aiBotPromptUpdatedAt } : updated);
          }}
        />

        {/* Profile editor */}
        <Card>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <SectionHeader title="Business profile" icon={<Info size={16} />} className="mb-0" />
            <Button variant="secondary" size="sm" disabled={busy} onClick={syncFromMeta} iconLeft={<RefreshCw size={13} />}>
              Sync from Meta
            </Button>
          </div>

          <FormField label="Profile picture" hint="Square, 640×640 or larger.">
            <div className="flex items-start gap-3">
              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border border-border bg-surface-alt flex items-center justify-center">
                {acc.profilePicUrl ? (
                  <img src={acc.profilePicUrl} alt="WhatsApp profile" className="h-full w-full object-cover" />
                ) : (
                  <Info size={24} className="text-text-muted" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <MediaUpload
                  mode="image"
                  value={acc.profilePicUrl}
                  onChange={(url, meta) => setAcc({ ...acc, profilePicUrl: url, profileImagekitFileId: meta?.fileId })}
                  folder="whatsapp/account/images"
                  compact
                />
                <div className="mt-1 text-2xs text-text-muted">Saving the profile also uploads the picture to WhatsApp.</div>
              </div>
            </div>
          </FormField>

          {acc.profileSyncedAt && <p className="mb-3 text-xs text-text-muted">Last confirmed by WhatsApp: {new Date(acc.profileSyncedAt).toLocaleString()}</p>}
          {acc.metaProfilePicUrl && <details className="mb-3 text-xs text-text-muted"><summary className="cursor-pointer">Meta picture URL · reference only</summary><p className="mt-2 break-all rounded-lg bg-surface-alt p-3">{acc.metaProfilePicUrl}</p></details>}
          <FormField label="Account label" hint="Shown inside the CRM only." required>
            <Input
              value={acc.businessName}
              onChange={(e) => setAcc({ ...acc, businessName: e.target.value })}
            />
          </FormField>
          <FormField label="About / status" hint={(acc.aboutStatus?.length ?? 0) > 139 ? 'Your longer About is kept. New text: 139 characters max.' : `${acc.aboutStatus?.length ?? 0}/139`}>
            <Input
              value={acc.aboutStatus ?? ''}
              onChange={(e) => setAcc({ ...acc, aboutStatus: e.target.value })}
              placeholder="A short description visible to WhatsApp users…" maxLength={139}
            />
          </FormField>
          <FormField label="Business description" hint={`${acc.businessDescription?.length ?? 0}/512`}>
            <Textarea rows={3} maxLength={512} value={acc.businessDescription ?? ''} onChange={e => setAcc({ ...acc, businessDescription: e.target.value })} placeholder="Tell customers what your business offers…" />
          </FormField>
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormField label="Business category">
              <SearchableSelect
                options={BUSINESS_CATEGORIES}
                value={acc.businessCategory ?? 'UNDEFINED'}
                onChange={(value) => setAcc({ ...acc, businessCategory: value })}
                numbered={false}
                searchPlaceholder="Search categories…"
                aria-label="Business category"
              />
            </FormField>
            <FormField label="Address">
              <Input
                value={acc.address ?? ''}
                onChange={(e) => setAcc({ ...acc, address: e.target.value })}
                iconLeft={<MapPin size={14} />}
              />
            </FormField>
            <FormField label="Email">
              <Input
                type="email"
                value={acc.emailAddress ?? ''}
                onChange={(e) => setAcc({ ...acc, emailAddress: e.target.value })}
                iconLeft={<Mail size={14} />}
              />
            </FormField>
            <FormField label="Website">
              <Input
                value={acc.website ?? ''}
                onChange={(e) => setAcc({ ...acc, website: e.target.value })}
                iconLeft={<Globe size={14} />}
                placeholder="https://…"
              />
            </FormField>
          </div>
          <div className="flex justify-end">
            <Button disabled={busy} onClick={save}>Save profile</Button>
          </div>
        </Card>
        <Card><SectionHeader title="Phone registration" icon={<Shield size={17} />} className="mb-3" subtitle="Complete Cloud API registration after connecting a new phone number." />{acc.registrationStatus === 'registered' ? <p className="text-sm text-success">Registered for the WhatsApp Cloud API.</p> : <><p className="mb-3 text-sm text-text-muted">Already sending messages? No action is needed. For a newly onboarded number, choose a six-digit two-step verification PIN and keep it somewhere safe.</p>{acc.registrationError && <p className="mb-3 text-xs text-danger">{acc.registrationError}</p>}<div className="flex max-w-md items-center gap-3"><Input type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} placeholder="Six-digit PIN" /><Button disabled={pin.length !== 6 || busy} onClick={async () => { setBusy(true); try { await whatsappAccountService.register(acc.accountUid, pin); acceptAccount(await whatsappAccountService.getById(acc.accountUid)); setPin(''); toast.success('Phone registered'); } catch { /* API handles error */ } finally { setBusy(false); } }}>Register</Button></div></>}</Card>

        {/* Bot mode — choose how this WhatsApp number replies to inbound */}
        <BotModeCard acc={acc} setAcc={setAcc} busy={busy} onSave={save} />

        {acc.aiAgentUid && <AgentAnswersCard agent={assignedAgent} accountUid={acc.accountUid} />}

        {acc.botMode === 'ai_chat' && (!acc.aiAgentUid || agentBorrowsThisNumber) && (
          <AiBotConfigCard
            cfg={acc.aiBotConfig ?? DEFAULT_AI_CONFIG}
            onChange={(next) => setAcc({ ...acc, aiBotConfig: next })}
            busy={busy}
            onSave={save}
          />
        )}

        {acc.botMode === 'ai_chat' && acc.aiBotCompiledPrompt && (
          <GeneratedPromptCard prompt={acc.aiBotCompiledPrompt} updatedAt={acc.aiBotPromptUpdatedAt} />
        )}

        {/* Bot-flow safety net — only relevant in 'flow' mode. */}
        {(acc.botMode ?? 'flow') === 'flow' && (
        <Card>
          <SectionHeader
            title="Bot flow safety net"
            icon={<Bot size={16} />}
            className="mb-3"
            subtitle="If a lead keeps replying with answers the bot can't match, pause the flow instead of re-sending the same prompt forever."
          />
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormField label="Max re-prompts per state" hintBelow hint="Default 2. After this many un-matching replies on the same step, the bot pauses.">
              <Input
                type="number"
                min={1}
                max={20}
                value={acc.maxFlowRetries ?? 2}
                onChange={(e) => setAcc({ ...acc, maxFlowRetries: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              />
            </FormField>
            <FormField label="Reset window (minutes)" hintBelow hint="Default 60. After this many minutes with no new retry, the counter resets.">
              <Input
                type="number"
                min={1}
                max={1440}
                value={acc.flowRetryWindowMinutes ?? 60}
                onChange={(e) => setAcc({ ...acc, flowRetryWindowMinutes: Math.max(1, parseInt(e.target.value, 10) || 1) })}
              />
            </FormField>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-[12rem] flex-1 text-xs text-text-muted">When the cap is hit the flow stops, the lead moves to <strong>contacted</strong>, and an agent can resume it from the lead.</p>
            <Button disabled={busy} onClick={save}>Save settings</Button>
          </div>
        </Card>
        )}

        {/* Tech credentials */}
        <Card>
          <SectionHeader title="Technical credentials" icon={<Shield size={16} />} className="mb-3"
            subtitle="Read-only. Contact your platform admin to modify these."
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'WABA ID',          value: acc.wabaId },
              { label: 'App ID',           value: acc.appId },
              { label: 'Phone number ID',  value: acc.phoneNumberId },
              { label: 'API base + ver',   value: `${acc.apiBaseUrl}${acc.apiVersion}` },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-lg border border-border bg-surface-alt px-3 py-2">
                <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">{label}</div>
                <div className="font-mono text-xs text-text-primary break-all">{value}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

// ─── Bot mode picker ────────────────────────────────────────────────────────

interface BotModeCardProps {
  acc: WhatsappAccount;
  setAcc: (a: WhatsappAccount) => void;
  busy: boolean;
  onSave: () => void;
}

function BotModeCard({ acc, setAcc, busy, onSave }: BotModeCardProps) {
  const current = acc.botMode ?? 'flow';
  const [depts, setDepts] = useState<Department[]>([]);

  useEffect(() => {
    departmentsService.list().then(setDepts).catch(() => setDepts([]));
  }, []);
  const modes: Array<{
    id: 'flow' | 'ai_chat' | 'off';
    label: string;
    description: string;
    icon: React.ReactNode;
  }> = [
    { id: 'flow',    label: 'Bot flows', description: 'Scripted bot flows trigger from ads/keywords. Best for structured lead qualification.',                                  icon: <Workflow size={18} /> },
    { id: 'ai_chat', label: 'AI chat',   description: 'Free-form conversational AI replies using your instructions + business profile. Stays on-topic via the services list.', icon: <Sparkles size={18} /> },
    { id: 'off',     label: 'Off',       description: 'No automated reply. Inbound messages still create lead records, but the bot stays silent.',                              icon: <PowerOff size={18} /> },
  ];

  return (
    <Card>
      <SectionHeader
        title="Bot mode"
        icon={<Bot size={16} />}
        className="mb-3"
        subtitle="How this number replies to incoming customer messages."
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {modes.map((m) => {
          const active = current === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setAcc({ ...acc, botMode: m.id })}
              className={clsx(
                'flex flex-col items-start gap-1 rounded-xl border px-3 py-2.5 text-left transition-colors',
                active
                  ? 'border-primary bg-primary-soft text-primary'
                  : 'border-border bg-surface text-text-secondary hover:border-primary/50',
              )}
            >
              <div className="flex items-center gap-2">
                <span className={clsx('flex h-7 w-7 items-center justify-center rounded-lg', active ? 'bg-primary text-white' : 'bg-surface-alt text-text-muted')}>
                  {m.icon}
                </span>
                <div className="text-sm font-semibold">{m.label}</div>
              </div>
              <div className="text-2xs text-text-muted">{m.description}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3">
            <span className="shrink-0 text-[13px] font-medium text-text-secondary">Capture department</span>
            <span className="ml-auto min-w-[7rem] flex-1 text-right text-2xs text-text-muted">
              {depts.length === 0 ? <>No departments yet — <Link to="/settings/departments" className="underline">add some</Link></> : 'Where new leads from this number land first'}
            </span>
          </div>
          <SearchableSelect
            options={[{ value: '', label: 'Company default' }, ...depts.map((d) => ({ value: d.deptUid, label: d.name, description: d.isDefault ? 'Default department' : undefined }))]}
            value={acc.captureDepartmentUid ?? ''}
            onChange={(value) => setAcc({ ...acc, captureDepartmentUid: value || undefined })}
            disabled={busy || depts.length === 0}
            numbered={false}
            searchable={depts.length > 6}
            aria-label="Capture department"
          />
        </div>
        <Button disabled={busy} onClick={onSave}>Save mode</Button>
      </div>
    </Card>
  );
}

// ─── AI bot config form ────────────────────────────────────────────────────

/**
 * Read-only view of the compiled system prompt the AI agent operates from.
 * Regenerated server-side on every save — gives the operator (and support)
 * exact visibility into what the bot was told, without exposing an edit
 * surface (edits belong in the structured config above).
 */
function GeneratedPromptCard({ prompt, updatedAt }: { prompt: string; updatedAt?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <SectionHeader
        title="Generated AI prompt"
        subtitle={`Compiled from your settings${updatedAt ? ` · updated ${new Date(updatedAt).toLocaleString()}` : ''}. The live agent also adds each customer's details and your latest catalog on every message.`}
        action={
          <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
            {open ? 'Hide' : 'View prompt'}
          </Button>
        }
      />
      {open && (
        <pre className="mt-3 max-h-96 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border bg-surface-alt/50 p-4 font-mono text-[11px] leading-relaxed text-text-secondary">
          {prompt}
        </pre>
      )}
    </Card>
  );
}

interface AiBotConfigCardProps {
  cfg: AiBotConfig;
  onChange: (next: AiBotConfig) => void;
  busy: boolean;
  onSave: () => void;
  /** Reused by AI Agents → Persona, where "on this number" doesn't apply. */
  subtitle?: string;
}

/** The AI setup form — a WhatsApp number's AI chat config, and an AI agent's own persona. */
export function AiBotConfigCard({ cfg, onChange, busy, onSave, subtitle }: AiBotConfigCardProps) {
  const { pageOn, label } = useWorkspace();
  const catalogWord = label('catalog');
  const modelValue: ModelPickerValue | undefined = cfg.modelProvider && cfg.model
    ? { provider: cfg.modelProvider, model: cfg.model }
    : undefined;

  return (
    <Card>
      <SectionHeader
        title="AI chat configuration"
        icon={<Sparkles size={16} />}
        className="mb-3"
        subtitle={subtitle ?? 'Instructions and limits the AI follows on this number. Off-topic questions get a polite redirect.'}
      />

      <FormField label="Business instructions" hint="Services, tone, do's and don'ts, offers.">
        <div className="relative">
          <div className="absolute right-2 top-2 z-10">
            <EnhancePromptButton
              value={cfg.instructions ?? ''}
              onChange={(v) => onChange({ ...cfg, instructions: v })}
              context="WhatsApp AI bot instructions"
              disabled={busy}
            />
          </div>
        <textarea
          rows={5}
          value={cfg.instructions ?? ''}
          onChange={(e) => onChange({ ...cfg, instructions: e.target.value })}
          placeholder="You are the assistant for Bloom Cosmetics. We sell skincare and makeup. Ask the customer what skin type they have before recommending products. Never quote prices we haven't set. If they ask about anything other than skincare or makeup, politely redirect."
          className="w-full resize-y rounded-xl border border-border bg-surface px-3 py-2 pr-28 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
        />
        </div>
      </FormField>

      <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
      <FormField label="Conversation mode" hint="Auto picks from your category.">
        <SearchableSelect
          options={CONVERSATION_MODES}
          value={cfg.mode ?? ''}
          onChange={(v) => onChange({ ...cfg, mode: v === '' ? undefined : (v as NonNullable<AiBotConfig['mode']>) })}
          numbered={false}
          searchable={false}
          size="lg"
          aria-label="Conversation mode"
        />
      </FormField>
        <FormField label="AI model" hint="Blank = company default.">
          <ModelPicker
            value={modelValue}
            onChange={(v) => onChange({ ...cfg, modelProvider: v.provider, model: v.model })}
            className="w-full"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-3">
        <FormField label="Language" hint="Mirrors the customer.">
          <Input
            value={cfg.language ?? 'en'}
            onChange={(e) => onChange({ ...cfg, language: e.target.value })}
            placeholder="en"
            maxLength={10}
          />
        </FormField>
        <FormField label="Max reply (chars)" hint="Keep it short.">
          <Input
            type="number"
            min={50}
            max={4000}
            value={cfg.maxResponseChars ?? 600}
            onChange={(e) => onChange({ ...cfg, maxResponseChars: Math.max(50, Math.min(4000, parseInt(e.target.value, 10) || 600)) })}
          />
        </FormField>
        <FormField label="Off-topic style">
          <div className="flex gap-2">
            {(['soft', 'strict'] as const).map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => onChange({ ...cfg, refusalStyle: style })}
                className={clsx(
                  'flex-1 rounded-lg border px-3 py-2 text-xs font-medium capitalize transition-colors',
                  (cfg.refusalStyle ?? 'soft') === style
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border text-text-secondary hover:border-primary/50',
                )}
              >
                {style === 'soft' ? 'Soft' : 'Strict'}
              </button>
            ))}
          </div>
        </FormField>
      </div>

      <FormField label="Greeting" hint="Optional first reply in a new chat.">
        <Input
          value={cfg.greeting ?? ''}
          onChange={(e) => onChange({ ...cfg, greeting: e.target.value })}
          placeholder="Hi! 👋 Welcome to Bloom Cosmetics — how can I help with your skincare today?"
          maxLength={800}
        />
      </FormField>

      {/* Catalog exposure comes from Custom Objects / Catalog; these switches only decide what the AI may reveal. */}
      <div className="mb-3 divide-y divide-border overflow-hidden rounded-xl border border-border">
        <ToggleRow
          checked={cfg.showServicesEnabled === true}
          onChange={(v) => onChange({ ...cfg, showServicesEnabled: v })}
          title="Share services when asked"
          hint={<>Describe items from <strong>Custom Objects</strong> only when the customer asks. Off keeps the AI in intake mode.</>}
        />
        <ToggleRow
          checked={cfg.showPricesEnabled === true}
          onChange={(v) => onChange({ ...cfg, showPricesEnabled: v })}
          disabled={cfg.showServicesEnabled !== true}
          title="Show prices when asked"
          hint={cfg.showServicesEnabled !== true ? 'Turn on "Share services" first.' : 'On for fixed catalogs (restaurant, retail). Off for scoped pricing — the AI says the team will quote.'}
        />
        {pageOn('catalog') && (
          <ToggleRow
            checked={cfg.useCatalog === true}
            onChange={(v) => onChange({ ...cfg, useCatalog: v })}
            disabled={cfg.showServicesEnabled !== true}
            title={`Answer from your ${catalogWord}`}
            hint={<>Only available items from your <Link to="/catalog" className="font-medium text-primary hover:underline">{catalogWord}</Link>, at current prices.</>}
          />
        )}
        <ToggleRow
          checked={cfg.useKnowledge === true}
          onChange={(v) => onChange({ ...cfg, useKnowledge: v })}
          title="Answer from your Knowledge base"
          hint={<>Your website pages, files and Q&amp;As in <Link to="/ai-agents/knowledge" className="font-medium text-primary hover:underline">Knowledge</Link>. Questions it can't answer are collected there for you.</>}
        />
        <ToggleRow
          checked={cfg.captureLeadEnabled !== false}
          onChange={(v) => onChange({ ...cfg, captureLeadEnabled: v })}
          icon={<MessageCircle size={13} />}
          title="Auto-capture qualified leads"
          hint="Mark the lead qualified and notify your team once intent and contact details are captured."
        />
        <ToggleRow
          checked={cfg.enableFeaturePitching === true}
          onChange={(v) => onChange({ ...cfg, enableFeaturePitching: v })}
          title="Allow feature pitching"
          hint="Off (B2B, services): answer only what's asked. On (retail): may describe what you offer when asked."
        />
      </div>

      {(['tasks', 'deals', 'tickets'] as const).some((module) => pageOn(module)) && (
        <div className="mb-3 overflow-hidden rounded-xl border border-border">
          <div className="border-b border-border bg-surface-alt/50 px-3 py-2 text-xs font-semibold text-text-secondary">Technology &amp; IT actions <span className="font-normal text-text-muted">· linked to the customer and passed to an active team member</span></div>
          <div className="divide-y divide-border">
            {([
              ['createSalesFollowUp', 'tasks', 'Create sales follow-up tasks', 'After qualification or a callback request.', cfg.createSalesFollowUp !== false],
              ['createDealsEnabled', 'deals', 'Create a deal when qualified', 'Added to your default pipeline; the team sets its value.', cfg.createDealsEnabled === true],
              ['createSupportTicketsEnabled', 'tickets', 'Create technical support tickets', 'Captures the issue and replies with the real ticket number.', cfg.createSupportTicketsEnabled !== false],
            ] as const).filter(([, module]) => pageOn(module)).map(([key, , title, hint, checked]) => (
              <ToggleRow key={key} checked={checked} disabled={busy} onChange={(v) => onChange({ ...cfg, [key]: v })} title={title} hint={hint} />
            ))}
          </div>
        </div>
      )}

      <UpsellEditor cfg={cfg} onChange={onChange} />

      <RequiredFieldsEditor
        value={cfg.requiredFields ?? []}
        onChange={(next) => onChange({ ...cfg, requiredFields: next })}
        disabled={busy}
      />

      <div className="flex justify-end">
        <Button disabled={busy} onClick={onSave}>Save AI config</Button>
      </div>
    </Card>
  );
}

// ─── Upsell editor ─────────────────────────────────────────────────────────

interface UpsellEditorProps {
  cfg: AiBotConfig;
  onChange: (next: AiBotConfig) => void;
}

const WHEN_OPTIONS: Array<{ value: UpsellWhenToOffer; label: string; hint: string }> = [
  { value: 'after_primary', label: 'After primary item', hint: 'Offer once the customer has committed to a primary service / product' },
  { value: 'on_match',      label: 'When trigger matches', hint: 'Offer when the customer\'s message contains the trigger keyword below' },
];

/**
 * Upsell / add-on offers editor. The bot picks AT MOST ONE upsell per
 * conversation — never two, never repeated if declined. Operator can
 * configure as many candidate offers as they want; the bot decides which
 * one fits based on the conversation context and the `whenToOffer` setting.
 *
 * Examples:
 *   - Restaurant: { label: "Today's special: 2 burgers + drink", amount: 30,
 *                   currency: "AED", whenToOffer: "after_primary" }
 *   - Tourism:    { label: "Cappadocia balloon ride", amount: 800,
 *                   currency: "AED", whenToOffer: "on_match", trigger: "turkey" }
 */
function UpsellEditor({ cfg, onChange }: UpsellEditorProps) {
  const items = cfg.upsellItems ?? [];
  const enabled = cfg.upsellEnabled === true;

  const update = (idx: number, patch: Partial<AiBotUpsellItem>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...cfg, upsellItems: next });
  };
  const add = () => onChange({
    ...cfg,
    upsellItems: [
      ...items,
      { label: '', amount: undefined, currency: 'USD', whenToOffer: 'after_primary' },
    ],
  });
  const remove = (idx: number) =>
    onChange({ ...cfg, upsellItems: items.filter((_, i) => i !== idx) });

  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-border">
      <ToggleRow
        checked={enabled}
        onChange={(v) => onChange({ ...cfg, upsellEnabled: v })}
        icon={<Sparkles size={13} />}
        title="Offer add-ons in chat"
        hint={<>At most one add-on per chat, never pushed again if declined.{cfg.showPricesEnabled !== true && enabled && <span className="text-warning"> Prices stay hidden while "Show prices" is off.</span>}</>}
      />

      {enabled && (
        <div className="space-y-2 border-t border-border bg-surface-alt/30 p-3">
          {items.length === 0 && (
            <div className="text-xs italic text-text-muted">No offers configured yet. Add one below.</div>
          )}
          {items.map((u, idx) => (
            <div key={idx} className="space-y-2 rounded-lg border border-border bg-surface p-2">
              <div className="flex items-start gap-2">
                <input
                  value={u.label}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  placeholder="e.g. Today's special: 2 burgers + drink"
                  className="flex-1 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
                  maxLength={160}
                />
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="text-text-muted hover:text-danger p-1"
                  aria-label="Remove offer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <input
                  type="number"
                  value={u.amount ?? ''}
                  onChange={(e) => update(idx, { amount: e.target.value === '' ? undefined : Number(e.target.value) })}
                  placeholder="Price"
                  className="col-span-3 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
                />
                <input
                  value={u.currency ?? ''}
                  onChange={(e) => update(idx, { currency: e.target.value.toUpperCase().slice(0, 8) })}
                  placeholder="USD"
                  className="col-span-2 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm uppercase"
                />
                <div className="col-span-7">
                  <SearchableSelect
                    options={WHEN_OPTIONS.map((o) => ({ value: o.value, label: o.label, description: o.hint }))}
                    value={u.whenToOffer ?? 'after_primary'}
                    onChange={(v) => update(idx, { whenToOffer: v as UpsellWhenToOffer })}
                    numbered={false}
                    searchable={false}
                    className="!h-[34px]"
                    aria-label="When to offer"
                  />
                </div>
              </div>
              {u.whenToOffer === 'on_match' && (
                <input
                  value={u.trigger ?? ''}
                  onChange={(e) => update(idx, { trigger: e.target.value })}
                  placeholder='Trigger keyword (e.g. "turkey", "balayage")'
                  className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm"
                  maxLength={120}
                />
              )}
            </div>
          ))}
          <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} onClick={add}>
            Add offer
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Required fields editor (drag-and-drop) ────────────────────────────────

interface RequiredFieldsEditorProps {
  value: AiBotRequiredField[];
  onChange: (next: AiBotRequiredField[]) => void;
  disabled?: boolean;
}

const FIELD_TYPES: { value: AiBotFieldType; label: string }[] = [
  { value: 'text',      label: 'Text' },
  { value: 'email',     label: 'Email' },
  { value: 'phone',     label: 'Phone' },
  { value: 'date',      label: 'Date' },
  { value: 'number',    label: 'Number' },
  { value: 'choice',    label: 'Choice (dropdown)' },
  { value: 'multiline', label: 'Long text' },
];

function RequiredFieldsEditor({ value, onChange, disabled }: RequiredFieldsEditorProps) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  const update = (idx: number, patch: Partial<AiBotRequiredField>) => {
    const next = [...value];
    next[idx] = { ...next[idx], ...patch };
    onChange(reorder(next));
  };

  const remove = (idx: number) => onChange(reorder(value.filter((_, i) => i !== idx)));

  const add = () => {
    const slug = `field_${value.length + 1}`;
    onChange(reorder([...value, { key: slug, label: '', type: 'text', required: false, order: value.length + 1 }]));
  };

  const loadDefaults = async () => {
    setLoadingDefaults(true);
    try {
      const defaults = await whatsappAccountService.getRequiredFieldDefaults();
      onChange(reorder(defaults));
      toast.success('Loaded industry-default fields. Edit anything you like.');
    } catch {
      toast.error('Could not load defaults');
    } finally {
      setLoadingDefaults(false);
    }
  };

  // Drag handlers — native HTML5 DnD, no extra deps.
  const onDragStart = (idx: number) => setDragIdx(idx);
  const onDragOver = (e: React.DragEvent) => e.preventDefault();
  const onDrop = (toIdx: number) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const next = [...value];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange(reorder(next));
    setDragIdx(null);
  };

  return (
    <FormField
      label="Required lead fields"
      hint="Captured before a lead is qualified. Drag to reorder."
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <Button variant="secondary" size="sm" iconLeft={<Download size={13} />} onClick={loadDefaults} loading={loadingDefaults} disabled={disabled}>
          Load industry defaults
        </Button>
        <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={add} disabled={disabled}>
          Add field
        </Button>
      </div>

      {value.length === 0 ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-surface-alt/30 px-3 py-3 text-xs text-text-muted">
          <ListChecks size={16} className="shrink-0 text-text-muted" />
          <span>No fields yet — <strong>Load industry defaults</strong> or <strong>Add field</strong>.</span>
        </div>
      ) : (
        <div className="space-y-1.5">
          {value.map((f, idx) => (
            <div
              key={idx}
              draggable={!disabled}
              onDragStart={() => onDragStart(idx)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(idx)}
              className={clsx(
                'group grid grid-cols-12 items-center gap-2 rounded-lg border bg-surface px-2 py-1.5 transition-colors',
                dragIdx === idx ? 'border-primary bg-primary-soft' : 'border-border hover:border-primary/40',
              )}
            >
              <div className="col-span-1 flex items-center justify-center text-text-muted cursor-grab active:cursor-grabbing">
                <GripVertical size={14} />
              </div>
              <input
                value={f.key}
                onChange={(e) => update(idx, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 60) })}
                placeholder="key"
                className="col-span-3 h-7 rounded-md border border-border bg-surface-alt px-2 font-mono text-xs"
                disabled={disabled}
              />
              <input
                value={f.label}
                onChange={(e) => update(idx, { label: e.target.value.slice(0, 120) })}
                placeholder="What the AI asks for"
                className="col-span-4 h-7 rounded-md border border-border bg-surface-alt px-2 text-xs"
                disabled={disabled}
              />
              <div className="col-span-2 min-w-0">
                <SearchableSelect
                  options={FIELD_TYPES}
                  value={f.type}
                  onChange={(v) => update(idx, { type: v as AiBotFieldType, options: v === 'choice' ? (f.options ?? []) : undefined })}
                  disabled={disabled}
                  numbered={false}
                  searchable={false}
                  className="!h-7 !px-2 !text-xs"
                  aria-label="Field type"
                />
              </div>
              <div className="col-span-1 flex justify-center" title="Required">
                <Switch checked={f.required} onChange={(v) => update(idx, { required: v })} disabled={disabled} label="Required" />
              </div>
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled}
                className="col-span-1 flex items-center justify-center rounded-md p-1 text-text-muted hover:bg-danger-soft hover:text-danger"
                title="Remove"
              >
                <Trash2 size={13} />
              </button>

              {/* Options editor — only when type === 'choice' */}
              {f.type === 'choice' && (
                <div className="col-span-12 col-start-2 mt-1.5">
                  <input
                    value={(f.options ?? []).join(', ')}
                    onChange={(e) => update(idx, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                    placeholder="Option A, Option B, Option C"
                    className="w-full rounded-md border border-border bg-surface-alt px-2 py-1 text-xs"
                    disabled={disabled}
                  />
                  <div className="mt-0.5 text-2xs text-text-muted">Comma-separated. AI will only accept one of these values.</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </FormField>
  );
}

/** Re-number `order` to match array index so the AI walks fields in the visual order. */
function reorder(fields: AiBotRequiredField[]): AiBotRequiredField[] {
  return fields.map((f, idx) => ({ ...f, order: idx + 1 }));
}

/** This number is answered by an AI agent: its persona, knowledge and hand-over rules apply here. */
function AgentAnswersCard({ agent, accountUid }: { agent: AiAgent | null; accountUid: string }) {
  return (
    <Card>
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"><Bot size={16} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-text-primary">Answered by AI agent {agent ? `“${agent.name}”` : ''}</div>
          <p className="mt-0.5 text-xs text-text-muted">
            {agent?.brainAccountUid === accountUid
              ? 'The agent uses this number\'s AI settings below, plus its own knowledge and hand-over rules.'
              : 'The agent\'s persona, knowledge and hand-over rules replace this number\'s AI settings. Bot mode must stay on AI chat.'}
            {agent && !agent.isActive && ' The agent is switched off, so this number gets no AI replies.'}
          </p>
        </div>
        {agent && <Link to={`/ai-agents/${agent.agentUid}?tab=persona`}><Button variant="secondary" size="sm">Open agent</Button></Link>}
      </div>
    </Card>
  );
}
